import { Controller } from "@hotwired/stimulus"
import { describeArc } from "@/lib/svgArcUtils"
import tailwindColors from "@/lib/tailwindColors"

interface DataItem {
  percent_of_total: number
  value: number
  currency: string
  fill_color: string
  bg_color: string
  label: string
}

export default class extends Controller {
  static targets = ["svg", "content"]
  static values = {
    data: Array,
    label: String,
  }

  declare readonly svgTarget: SVGSVGElement
  declare readonly contentTarget: HTMLDivElement
  declare readonly dataValue: DataItem[]
  declare readonly labelValue: string

  private readonly viewboxSize = 200
  private readonly strokeWidth = 8
  private readonly padAngle = 3 // degrees between segments

  private paths: SVGPathElement[] = []

  connect() {
    this.draw()
    document.addEventListener("turbo:load", this.redraw)
  }

  disconnect() {
    this.teardown()
    document.removeEventListener("turbo:load", this.redraw)
  }

  private redraw = () => {
    this.teardown()
    this.draw()
  }

  private teardown() {
    this.paths = []
    while (this.svgTarget.firstChild) {
      this.svgTarget.removeChild(this.svgTarget.firstChild)
    }
  }

  private draw() {
    const cx = this.viewboxSize / 2
    const cy = this.viewboxSize / 2
    const outerRadius = this.viewboxSize / 2
    const innerRadius = outerRadius - this.strokeWidth

    // Calculate total for percentages
    const total = this.dataValue.reduce((sum, d) => sum + d.percent_of_total, 0)
    if (total === 0) return

    // Create SVG group for arcs
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
    this.svgTarget.appendChild(g)

    // Build arcs
    let currentAngle = 0
    const halfPad = this.padAngle / 2

    this.dataValue.forEach((item, index) => {
      const sweepAngle = (item.percent_of_total / total) * 360

      // Add padding between segments (but not for single-item charts)
      const startAngle = currentAngle + (this.dataValue.length > 1 ? halfPad : 0)
      const endAngle = currentAngle + sweepAngle - (this.dataValue.length > 1 ? halfPad : 0)

      const pathD = describeArc(cx, cy, innerRadius, outerRadius, startAngle, endAngle)

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("d", pathD)
      path.classList.add(item.fill_color)
      path.dataset.index = String(index)
      path.style.cursor = "pointer"
      path.style.transition = "opacity 0.15s ease"

      // Event listeners
      path.addEventListener("mouseenter", () => this.handleMouseEnter(index))
      path.addEventListener("mouseleave", () => this.handleMouseLeave())

      g.appendChild(path)
      this.paths.push(path)

      currentAngle += sweepAngle
    })

    // Show summary in center
    this.showSummary()
  }

  private handleMouseEnter(activeIndex: number) {
    // Dim all segments except the active one
    this.paths.forEach((path, index) => {
      if (index === activeIndex) {
        // Keep original color
        path.classList.remove("fill-gray-200")
        path.classList.add(this.dataValue[index].fill_color)
      } else {
        // Dim to gray
        path.classList.remove(this.dataValue[index].fill_color)
        path.classList.add("fill-gray-200")
      }
    })

    // Show detail in center
    this.showDetail(this.dataValue[activeIndex])
  }

  private handleMouseLeave() {
    // Restore all original colors
    this.paths.forEach((path, index) => {
      path.classList.remove("fill-gray-200")
      path.classList.add(this.dataValue[index].fill_color)
    })

    // Show summary in center
    this.showSummary()
  }

  private showSummary() {
    const total = this.dataValue.reduce((sum, d) => sum + d.value, 0)
    const currency = this.dataValue[0]?.currency || "USD"
    this.contentTarget.innerHTML = `${this.formatCurrencyHtml({ value: total, currency })} <span class="text-xs">${this.labelValue}</span>`
  }

  private showDetail(item: DataItem) {
    this.contentTarget.innerHTML = `
      <span>${this.formatCurrencyHtml(item)}</span>
      <div class="flex flex-row text-xs gap-2 items-center">
        <div class="w-[10px] h-[10px] rounded-full ${item.bg_color}"></div>
        <span>${item.label}</span>
        <span>${item.percent_of_total}%</span>
      </div>
    `
  }

  private formatCurrencyHtml(datum: { value: number; currency: string }): string {
    const formattedValue = Intl.NumberFormat(undefined, {
      style: "currency",
      currency: datum.currency,
      currencyDisplay: "narrowSymbol",
    }).format(datum.value)

    const firstDigitIndex = formattedValue.search(/\d/)
    const currencyPrefix = formattedValue.substring(0, firstDigitIndex)
    const mainPart = formattedValue.substring(firstDigitIndex)
    const [integerPart, fractionalPart] = mainPart.split(".")

    return `<p class="text-gray-500 -space-x-0.5">${currencyPrefix}<span class="text-xl text-gray-900 font-medium">${integerPart}</span>${fractionalPart ? `.${fractionalPart}` : ""}</p>`
  }
}
